/**
 * AdminDashboard Component
 *
 * Main admin dashboard providing overview and navigation to all admin functions.
 * Serves as the central hub for the administration console.
 */

import React, { useState, useEffect } from 'react';
import { AdminLayout } from '@/components/shared/layouts';
import { Card, CardHeader, CardBody, Button, Link, Chip } from '@enfomentis/ui';
import { Settings, BarChart3, Shield, Database } from 'lucide-react';
import { Link as RouterLink } from 'react-router-dom';
import { betaApplicationService } from '@/services/BetaApplicationService';
import { userService } from '@/services/UserService';
import { BetaApplicationCard } from './BetaApplicationCard';
import { UserManagementCard } from './UserManagementCard';
import type { BetaApplicationStatus } from '@/types/supabase/BetaApplication';
import type { UserInvitationStats } from '@/types/supabase/User';

/**
 * Admin Dashboard Component
 *
 * Features:
 * - Overview of admin system capabilities
 * - Quick access to all admin functions
 * - Status indicators and metrics (future enhancement)
 * - Responsive design for desktop and mobile admin use
 * - Integration points for contractor enhancements
 *
 * TODO: [CONTRACTOR] Implement real-time authenticated user session tracking - replace hardcoded activeSessions (0) with live count of currently logged-in users (useAuth hook integration, session management, real-time updates)
 * TODO: [CONTRACTOR] Add critical service health monitoring - implement status checks for Supabase database connectivity and email service availability with health indicators displayed in admin dashboard
 */
export const AdminDashboard: React.FC = () => {
  // State for real system stats
  const [betaApplicationCount, setBetaApplicationCount] = useState<number>(0);
  const [statusCounts, setStatusCounts] = useState<Record<BetaApplicationStatus, number>>({
    pending: 0,
    approved: 0,
    waitlisted: 0,
    rejected: 0,
    activated: 0
  });
  const [userStats, setUserStats] = useState<UserInvitationStats | null>(null);

  // Load real data from both services
  useEffect(() => {
    const loadStats = async () => {
      try {
        const [applications, counts, userInvitationStats] = await Promise.all([
          betaApplicationService.getAll(),
          betaApplicationService.getStatusCounts(),
          userService.getInvitationStats()
        ]);
        setBetaApplicationCount(applications.length);
        setStatusCounts(counts);
        setUserStats(userInvitationStats);
      } catch (error) {
        console.error('Failed to load admin stats:', error);
      }
    };

    loadStats();
  }, []);

  // Admin function cards data (excluding Beta Applications and User Management - handled by dedicated cards)
  const adminFunctions = [
    {
      icon: <BarChart3 className="w-6 h-6" />,
      title: "Analytics",
      description: "View system usage and performance metrics",
      href: "/admin/analytics",
      status: "Coming Soon",
      color: "success" as const
    },
    {
      icon: <Database className="w-6 h-6" />,
      title: "Data Management",
      description: "Database maintenance and backup operations",
      href: "/admin/data",
      status: "Coming Soon",
      color: "warning" as const
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Security",
      description: "Security settings and access control",
      href: "/admin/security",
      status: "Coming Soon",
      color: "danger" as const
    },
    {
      icon: <Settings className="w-6 h-6" />,
      title: "System Settings",
      description: "Configure admin preferences and system settings",
      href: "/admin/settings",
      status: "Coming Soon",
      color: "default" as const
    }
  ];

  const systemStats = {
    totalUsers: userStats?.totalUsers || 0,
    betaApplications: betaApplicationCount,
    activeSessions: 0,
    systemUptime: "100%"
  };

  return (
    <AdminLayout title="Admin Dashboard">
      <div className="space-y-6">
        {/* System Overview */}
        <Card shadow="sm">
          <CardHeader>
            <h3 className="text-lg font-semibold text-text-primary">System Overview</h3>
          </CardHeader>
          <CardBody>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-primary">{systemStats.totalUsers}</p>
                <p className="text-sm text-text-secondary">Total Users</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-secondary">{systemStats.betaApplications}</p>
                <p className="text-sm text-text-secondary">Beta Applications</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-success">{systemStats.activeSessions}</p>
                <p className="text-sm text-text-secondary">Active Sessions</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-warning">{systemStats.systemUptime}</p>
                <p className="text-sm text-text-secondary">Uptime</p>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Admin Functions Grid */}
        <div>
          <h3 className="text-lg font-semibold text-text-primary mb-4">Administration Functions</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Dedicated dashboard cards for main functions */}
            <BetaApplicationCard
              betaApplicationCount={betaApplicationCount}
              statusCounts={statusCounts}
            />
            <UserManagementCard />

            {/* Standard cards for other functions */}
            {adminFunctions.map((func) => (
              <Card key={func.href} shadow="sm" className="hover:shadow-md transition-shadow">
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg bg-${func.color} bg-opacity-10`}>
                      {func.icon}
                    </div>
                    <div>
                      <h4 className="font-semibold text-text-primary">{func.title}</h4>
                      <div className="flex gap-2 flex-wrap">
                        <Chip size="sm" color={func.color} variant="flat">
                          {func.status}
                        </Chip>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardBody className="pt-0">
                  <p className="text-sm text-text-secondary mb-3">
                    {func.description}
                  </p>
                  {func.status === "Available" ? (
                    <RouterLink to={func.href} className="w-full">
                      <Button
                        color={func.color}
                        variant="flat"
                        size="sm"
                        className="w-full"
                      >
                        Access {func.title}
                      </Button>
                    </RouterLink>
                  ) : (
                    <Button
                      color={func.color}
                      variant="flat"
                      size="sm"
                      isDisabled
                      className="w-full"
                    >
                      {func.status}
                    </Button>
                  )}
                </CardBody>
              </Card>
            ))}
          </div>
        </div>

        {/* Development Information */}
        <Card shadow="sm" className="border border-warning-200 bg-warning-50 dark:bg-warning-900/10">
          <CardHeader>
            <h3 className="text-lg font-semibold text-warning-800 dark:text-warning-200">
              Development Mode
            </h3>
          </CardHeader>
          <CardBody>
            <div className="space-y-3">
              <p className="text-sm text-warning-700 dark:text-warning-300">
                This admin interface is currently in development mode with basic authentication.
              </p>
              <div className="text-xs text-warning-600 dark:text-warning-400 space-y-1">
                <p>• <strong>Current Protection:</strong> Simple password authentication</p>
                <p>• <strong>Data Storage:</strong> Local browser storage (development)</p>
                <p>• <strong>Session Management:</strong> Basic localStorage implementation</p>
                <p>• <strong>Contractor Enhancement:</strong> Security and authentication upgrade planned</p>
              </div>
              <div className="mt-4">
                <Link
                  href="https://github.com/anthropics/claude-code/issues"
                  isExternal
                  className="text-sm text-warning-700 dark:text-warning-300"
                >
                  Report Issues or Feedback →
                </Link>
              </div>
            </div>
          </CardBody>
        </Card>
      </div>
    </AdminLayout>
  );
};